import { useQuery } from '@apollo/client';
import SettingsIcon from '@mui/icons-material/Settings';
import { useRecoilValue } from 'recoil';

import ELEMENT from 'common/element';
import { Permission } from 'common/graphqlTypes';

import PageHeader from 'components/common/PageHeader/PageHeader';
import Page from 'components/common/Page/Page';
import Badge from 'components/common/Badge/Badge';
import { hasPermissionState } from 'components/account/account.state';
import { HELP_ROUTE } from 'components/help/constants';
import { TeamTable, UserAddForm } from './components';

import './UserAccountPage.scss';
import { UserAccountData } from './types';
import { USER_ACCOUNT_LIST } from './queries';

/**
 * Page that allows user with admin rights to manage access/restrictions per user
 */
const UserAccountPage = () => {
  // Changes made to the user list in /scalyr/logs do not reflect on the Manage Users page
  // For now, we are using a fetchPolicy of network-only to ensure we always have the correct data
  const {
    loading: userAccountLoading,
    data: userAccountData,
    refetch,
  } = useQuery<UserAccountData>(USER_ACCOUNT_LIST, { fetchPolicy: 'network-only' });

  const hasFullAccessPermission = useRecoilValue(hasPermissionState(Permission.fullAccess));

  return (
    <Page docTitle="User Accounts" testId={ELEMENT.userAccountsPage}>
      <div className="UserAccountPage">
        <PageHeader
          title="User Accounts"
          helpLink={HELP_ROUTE.HELP_USERS}
          additionalRightButtons={[
            {
              name: 'Advanced Configuration',
              icon: <SettingsIcon fontSize="small" />,
              link: '/file?path=/scalyr/logs',
              testId: ELEMENT.userAccountsAdvancedConfigurationButton,
            },
          ]}
        />
        <div>
          <h4 className="SectionHeader UserAccountPage__Header">Add New User(s)</h4>
          <UserAddForm refetchData={refetch} userAccountData={userAccountData} disabled={!hasFullAccessPermission} />
          <h4 className="SectionHeader UserAccountPage__Header">
            My Team &nbsp;
            <Badge variant="info">{userAccountData?.userAccounts?.length || 0}</Badge>
          </h4>
          <TeamTable
            userAccountLoading={userAccountLoading}
            userAccountData={userAccountData}
            refetchData={refetch}
            isWriteDisabled={!hasFullAccessPermission}
          />
        </div>
      </div>
    </Page>
  );
};

export default UserAccountPage;
